import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import classNames from "classnames";

// components
import GradeScaleDetailModal from "../GradeScaleDetailModal";

// design system
import {
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import { EyeOutlined } from "@toddle-edu/ds-icons";

// modules
import { getGradeScalesQueryEnhancer } from "../../modules/AssessmentGradeScaleToolEnhancers";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";

// constants
import {
  ASSESSMENT,
  GRADE_SCALE_TOOL,
  STANDARD_BASED_RUBRIC,
} from "Constants/stringConstants";

// utils
import { getIsToolPresent, scrollIntoView } from "Utils";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/LearningCourseUtils";

// scss
import classes from "./AssessmentGradeScaleToolEdit.scss";

const styles = {
  button: {
    minWidth: "220px",
  },
};

const BUTTON_ID = "GRADE_SCALE_SELECTION_BUTTON";

const AssessmentGradeScaleToolEdit = memo(props => {
  const {
    assessmentTool,
    gradeScales = [],
    isGradeScaleLoading,
    customRef,
    createAssessmentTool,
    assessmentId,
    assessmentTools,
  } = props;

  const { t } = useI18n();

  const [selectedValue, setSelectedValue] = useState(assessmentTool?.id);
  const [hasError, setHasError] = useState(false);
  const [selectedGradeScale, setSelectedGradeScale] = useState(null);

  const selectedOption = useMemo(() => {
    const foundOption = _.find(gradeScales, { key: selectedValue });
    if (foundOption) return foundOption;

    if (!_.isEmpty(assessmentTool)) {
      const { id, gradeScale } = assessmentTool || {};

      return {
        key: id,
        gradeScaleId: gradeScale?.id,
        label: gradeScale?.displayName,
      };
    }

    return {};
  }, [gradeScales, selectedValue, assessmentTool]);

  useEffect(() => {
    if (!_.isFunction(customRef)) return;

    const isValid = () => {
      if (_.isEmpty(selectedValue)) {
        const element = document.getElementById(BUTTON_ID);

        if (element) {
          scrollIntoView({ element, block: "center", inline: "center" });
          setHasError(true);
        }

        return 1;
      }

      setHasError(false);
      return 0;
    };

    customRef({ isValid });
  }, [selectedValue, customRef]);

  const onSelectValue = useCallback(
    async ({ key }) => {
      if (key === selectedValue) return;

      setSelectedValue(key);
      setHasError(false);

      await createAssessmentTool({
        type: GRADE_SCALE_TOOL,
        parentId: assessmentId,
        parentType: ASSESSMENT,
        extraParams: { gradeScaleEntityMapId: key },
      });
    },
    [assessmentId, selectedValue, createAssessmentTool]
  );

  const onClickPreview = useCallback(() => {
    setSelectedGradeScale(selectedOption?.key);
  }, [selectedOption]);

  const onCloseModal = useCallback(() => {
    setSelectedGradeScale(null);
  }, []);

  const disabled = useMemo(() => {
    return (
      isGradeScaleLoading ||
      _.size(gradeScales) <= 1 ||
      getIsToolPresent(assessmentTools, STANDARD_BASED_RUBRIC)
    );
  }, [isGradeScaleLoading, gradeScales, assessmentTools]);

  // TODO: @shivam-toddleapp localise and confirm text
  const disabledButtonTooltip = useMemo(() => {
    if (_.size(gradeScales) <= 1) {
      return "Only one grade scale is available to choose from";
    }

    if (getIsToolPresent(assessmentTools, STANDARD_BASED_RUBRIC)) {
      return "Grade scale added based on standard based rubric";
    }

    return "";
  }, [gradeScales, assessmentTools]);

  const renderButtonLabel = () => {
    if (selectedOption?.label) {
      return selectedOption.label;
    } else {
      return <span className={classes.selectLabel}>{t("common:select")}</span>;
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classNames(classes.label, "text-label")}>
          {t("academicSetup:grade_scale")}
        </div>

        <div className={classes.containerRight}>
          {/* <Tooltip
            placement="top"
            visible={disabled && disabledButtonTooltip}
            tooltip={disabledButtonTooltip}
          > */}
          <Dropdown
            overlay={
              <DropdownMenu options={gradeScales} onClick={onSelectValue} />
            }
            disabled={disabled}
          >
            <ButtonDisclosure
              id={BUTTON_ID}
              disabled={disabled}
              size={"medium"}
              variant={"neutral"}
              type={"outlined"}
              style={styles.button}
              className={classNames({
                [classes.buttonError]: hasError,
              })}
            >
              {renderButtonLabel()}
            </ButtonDisclosure>
          </Dropdown>
          {/* </Tooltip> */}

          {!_.isEmpty(selectedOption) ? (
            <Tooltip
              tooltip={t("common:view_with_label", {
                label: t("common:lowercase", {
                  text: t("academicSetup:grade_scale"),
                }),
              })}
              placement={"top"}
              showArrow={true}
            >
              <IconButton
                icon={<EyeOutlined variant={"subtle"} />}
                variant={"neutral"}
                type={"plain"}
                size={"small"}
                onClick={onClickPreview}
                disabled={_.isEmpty(selectedOption?.gradeScaleId)}
              />
            </Tooltip>
          ) : null}
        </div>
      </div>

      {selectedGradeScale ? (
        <GradeScaleDetailModal
          toolId={selectedGradeScale}
          onClose={onCloseModal}
        />
      ) : null}
    </>
  );
});

AssessmentGradeScaleToolEdit.displayName = "AssessmentGradeScaleToolEdit";

const mapStateToProps = (state, ownProps) => {
  const { learningCourseId: ownPropsLearningCourseId } = ownProps;

  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const learningCourseId =
    _.get(state, "planner.unitPlanData.learningCourseId", null) ||
    getSelectedLearningCourseId(state) ||
    ownPropsLearningCourseId;

  return {
    curriculumProgramId,
    curriculumProgramType,
    learningCourseId,
    academicYearId,
    fetchPolicy: "cache-first", // for getGradeScalesQueryEnhancer
  };
};

const mapActionCreators = { createAssessmentTool };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getGradeScalesQueryEnhancer
)(AssessmentGradeScaleToolEdit);
